import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAllTradesForAnalytics, getRoutinesByUser, getGoalsByUser, getAccountsByUser, getAccountGroupsByUser, getTradingStrategiesByUser, getTradingSessionsByUser, updateGoalProgress, completeRoutineItem, generateCheckoutSession, useQuery, useAction } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { PENDING_PLAN_KEY } from "../auth/SignupPage";
import { parsePaymentPlanId } from "../payment/plans";
import PlanSelectionModal from "./PlanSelectionModal";
import AddTradeModal from "../trading-journal/AddTradeModal";
import PromoteToFundedModal from "../trading-accounts/PromoteToFundedModal";
import { PaymentPlanId, SubscriptionStatus } from "../payment/plans";
import DashboardView from "./DashboardView";
import { useRealtimeSync } from "../client/hooks/useRealtimeSync";

/**
 * Cadence de rafraichissement du calendrier pour un utilisateur en temps reel.
 *
 * 90 s, soit quatre fois moins souvent que le journal : `getAllTradesForAnalytics`
 * n'est pas paginee et rend l'integralite des trades de l'utilisateur. Le
 * calendrier se lit a la journee, il n'a pas besoin de la meme fraicheur qu'une
 * table de trades.
 */
const REALTIME_CALENDAR_REFETCH_MS = 90_000;

export default function TradingDashboardPage() {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAddTradeModal, setShowAddTradeModal] = useState(false);
  const [fundedTransitionTarget, setFundedTransitionTarget] = useState<any | null>(null);
  const generateCheckoutSessionFn = useAction(generateCheckoutSession);

  const { data: sessions } = useQuery(getTradingSessionsByUser);

  // Handle pending plan checkout on first dashboard visit
  useEffect(() => {
    if (!user) return;

    // Un abonné payant n'a rien à choisir : lui proposer un plan le jour de son
    // inscription, c'est demander de payer à quelqu'un qui vient de le faire.
    // Le parcours landing -> checkout Stripe -> dashboard tombe exactement dans
    // ce cas, puisque l'inscription et le paiement ont lieu le même jour.
    const hasActivePaidPlan =
      !!user.subscriptionStatus &&
      user.subscriptionStatus !== SubscriptionStatus.Deleted &&
      !!user.subscriptionPlan &&
      user.subscriptionPlan !== PaymentPlanId.Free;

    const userCreatedAt = new Date(user.createdAt);
    const today = new Date();
    const isNewUser =
      !hasActivePaidPlan && userCreatedAt.toDateString() === today.toDateString();

    if (isNewUser) {
      // If the user came from the landing with a specific plan, redirect to Stripe immediately
      const pendingPlan = sessionStorage.getItem(PENDING_PLAN_KEY);
      if (pendingPlan) {
        sessionStorage.removeItem(PENDING_PLAN_KEY);
        try {
          const planId = parsePaymentPlanId(pendingPlan);
          generateCheckoutSessionFn(planId)
            .then((result) => {
              if (result?.sessionUrl) {
                window.location.href = result.sessionUrl;
              }
            })
            .catch(() => {
              // Fallback: show plan modal if checkout fails
              setShowPlanModal(true);
            });
        } catch {
          // Invalid plan id in sessionStorage — show modal
          setShowPlanModal(true);
        }
        return;
      }

      // No pending plan: show plan selection modal on first visit
      const hasShownPlanModalKey = `plan-modal-shown-${user.id}`;
      if (!localStorage.getItem(hasShownPlanModalKey)) {
        setTimeout(() => {
          setShowPlanModal(true);
          localStorage.setItem(hasShownPlanModalKey, "true");
        }, 1000);
      }
    }
  }, [user]);

  // Check if user is on Free plan (no active paid subscription)
  const isFreePlan = useMemo(() => {
    if (!user) return false;
    if (!user.subscriptionPlan || user.subscriptionPlan === PaymentPlanId.Free) return true;
    if (!user.subscriptionStatus || user.subscriptionStatus === SubscriptionStatus.Deleted) return true;
    return false;
  }, [user]);

  const { data: dashboardAccountsData } = useQuery(getAccountsByUser);
  const dashboardAccounts = (dashboardAccountsData as any)?.accounts ?? [];
  const { data: dashboardGroups } = useQuery(getAccountGroupsByUser);
  const { data: dashboardStrategies = [] } = useQuery(getTradingStrategiesByUser);

  // Fetch all trades for analytics (calendar + stats)
  // We pass a broad date range so the view can filter locally
  // excludeInactive: false so the "all + inactive" filter in DashboardView has data to work with
  //
  // ── RAFRAICHISSEMENT TEMPS REEL DU CALENDRIER ─────────────────────────────
  //
  // Le calendrier se nourrit de CETTE query, et sans rafraichissement un trade
  // ecrit en quelques secondes par le socket n'y apparaitrait qu'au
  // rechargement de la page.
  //
  // ⚠️ CADENCE VOLONTAIREMENT PLUS LENTE QUE LE JOURNAL (90 s contre 20 s), et
  // la raison est le COUT : cette query n'est PAS paginee. Elle rend tous les
  // trades de l'utilisateur, une vingtaine de colonnes, avec deux jointures
  // (strategies, tags), et resout au passage les comptes visibles. Sur un gros
  // journal, chaque rafraichissement est lourd -- la poller au rythme du
  // journal multiplierait ce cout par quatre pour un calendrier qui, lui, se
  // lit a la journee.
  //
  // `false` pour tout le monde sauf les utilisateurs en temps reel : les
  // autres n'ont rien de neuf a lire entre deux cycles de 15 minutes.
  const { isLive: realtimeLive, totalsArePartial } = useRealtimeSync();
  const { data: allTrades } = useQuery(
    getAllTradesForAnalytics,
    { excludeInactive: false },
    { refetchInterval: realtimeLive ? REALTIME_CALENDAR_REFETCH_MS : false },
  );

  const { data: routines, refetch: refetchRoutines } = useQuery(getRoutinesByUser);
  const { data: goals, refetch: refetchGoals } = useQuery(getGoalsByUser);

  const updateProgressFn = useAction(updateGoalProgress);
  const completeRoutineFn = useAction(completeRoutineItem);
  const hasUpdatedGoalProgress = useRef(false);

  const handleCompleteRoutine = async (routine: any) => {
    try {
      await completeRoutineFn({
        routineId: routine.id,
        completedItems: routine.checklistItems,
        date: new Date(),
      });
      refetchRoutines();
    } catch (_) {}
  };

  // Trigger goal progress update on dashboard load
  useEffect(() => {
    if (goals && goals.length > 0 && !hasUpdatedGoalProgress.current) {
      const hasActiveGoals = goals.some((g) => !g.isCompleted);
      if (hasActiveGoals) {
        hasUpdatedGoalProgress.current = true;
        updateProgressFn(undefined)
          .then(() => { setTimeout(() => refetchGoals(), 100); })
          .catch(() => {});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals?.length]);

  return (
    <>
      <DashboardView
        trades={allTrades ?? []}
        accounts={dashboardAccounts ?? []}
        sessions={sessions ?? []}
        routines={routines ?? []}
        goals={goals ?? []}
        accountGroups={dashboardGroups ?? []}
        strategies={dashboardStrategies ?? []}
        readOnly={false}
        totalsArePartial={totalsArePartial}
        isFreePlan={isFreePlan}
        showPlanModal={showPlanModal}
        onAddTrade={() => setShowAddTradeModal(true)}
        onPromoteToFunded={(account) => setFundedTransitionTarget(account)}
        onCompleteRoutine={handleCompleteRoutine}
        onOpenPlanModal={() => setShowPlanModal(true)}
      />

      {/* Add Trade Modal */}
      <AddTradeModal
        open={showAddTradeModal}
        onOpenChange={setShowAddTradeModal}
        onImportDone={() => {}}
        onImportCSV={() => {
          navigate("/trading-journal?import=csv");
        }}
      />

      {/* Plan Selection Modal */}
      <PlanSelectionModal
        open={showPlanModal}
        onOpenChange={setShowPlanModal}
      />

      {/* Promote to Funded Modal */}
      {fundedTransitionTarget && (
        <PromoteToFundedModal
          open={!!fundedTransitionTarget}
          onOpenChange={(v) => { if (!v) setFundedTransitionTarget(null); }}
          accountId={fundedTransitionTarget.id}
          initialBalance={fundedTransitionTarget.initialBalance}
          trades={(fundedTransitionTarget.trades ?? []).map((tr: any) => ({ date: typeof tr.date === "string" ? tr.date : new Date(tr.date).toISOString() }))}
          onPromoted={() => { setFundedTransitionTarget(null); window.location.reload(); }}
        />
      )}
    </>
  );
}
